import type { ReactNode } from 'react';
import { Keys, MOD_KEY } from '../components/Kbd';
import { Modal } from '../components/Modal';
import { useCurrentSession } from '../stores/session';
import { useUi } from '../stores/ui';
import { navItemsFor } from './nav-config';
import styles from './ShortcutSheet.module.css';

// `?` opens this. 06-qol §2: it shows global shortcuts and any specific to the
// current screen.

interface Shortcut {
  readonly keys: readonly string[];
  readonly description: string;
  /** `g` then a letter — rendered with a "then" between the halves. */
  readonly isSequence?: boolean;
}

const GLOBAL: readonly Shortcut[] = [
  { keys: [MOD_KEY, 'K'], description: 'Open the command palette' },
  { keys: ['?'], description: 'Show this sheet' },
  { keys: ['/'], description: 'Focus the search or filter on a list' },
  { keys: ['Esc'], description: 'Close a menu, dialog or drawer' },
];

const LISTS: readonly Shortcut[] = [
  { keys: ['↑', '↓'], description: 'Move between rows' },
  { keys: ['Enter'], description: 'Open the focused row' },
  { keys: ['Space'], description: 'Select the focused row' },
  { keys: ['Shift', 'click'], description: 'Select a range of rows' },
  { keys: [MOD_KEY, 'click'], description: 'Add or remove one row' },
];

const FORMS: readonly Shortcut[] = [
  { keys: [MOD_KEY, 'Enter'], description: 'Submit the form' },
  { keys: ['Esc'], description: 'Cancel and close' },
  { keys: ['Tab'], description: 'Next field' },
];

export function ShortcutSheet(): ReactNode {
  const isOpen = useUi((state) => state.isShortcutSheetOpen);
  const close = useUi((state) => state.closeShortcutSheet);
  const session = useCurrentSession();

  // Only the destinations this user can actually reach — a shortcut to a module
  // they cannot open is a shortcut that does nothing.
  const navigation: readonly Shortcut[] = navItemsFor(session)
    .filter((item) => item.goKey !== undefined)
    .map((item) => ({
      keys: ['g', item.goKey ?? ''],
      description: `Go to ${item.label.toLowerCase()}`,
      isSequence: true,
    }));

  return (
    <Modal isOpen={isOpen} onClose={close} title="Keyboard shortcuts">
      <div className={styles.grid}>
        <Group title="Global" shortcuts={GLOBAL} />
        {navigation.length > 0 && <Group title="Navigate" shortcuts={navigation} />}
        <Group title="Lists and tables" shortcuts={LISTS} />
        <Group title="Forms" shortcuts={FORMS} />
      </div>
    </Modal>
  );
}

function Group({
  title,
  shortcuts,
}: {
  title: string;
  shortcuts: readonly Shortcut[];
}): ReactNode {
  return (
    <div className={styles.group}>
      <span className={styles.groupTitle}>{title}</span>
      {shortcuts.map((shortcut) => (
        <div key={shortcut.description} className={styles.row}>
          <span className={styles.description}>{shortcut.description}</span>
          <span className={styles.keys}>
            {shortcut.isSequence ? (
              <>
                <Keys keys={[shortcut.keys[0]]} />
                <span className={styles.then}>then</span>
                <Keys keys={[shortcut.keys[1]]} />
              </>
            ) : (
              <Keys keys={shortcut.keys} />
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
